import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import CampaignResults from "../components/CampaignResults";
import PageHeader from "../components/PageHeader";
import {
  BULK_INVITE_WITH_USER_IDS,
  VALIDATE_USERS_FOR_EMAIL_BULK_INVITE,
} from "../gql/mutations/event";
import { INVITE_CAMPAIGN, RECORD_CAMPAIGN_INVITES } from "../gql/queries/inviteCampaigns";
import { Spacing } from "../types/enum";
import { type ZoneLabel } from "../types/graphql";

interface CampaignMember {
  userId: string;
  email: string;
  firstName?: string | null;
  isGuest: boolean;
  /** Why the invitation rules chose this event for her. */
  why?: string | null;
  /** Why she is on the list at all, when the list's own rule wouldn't have her. */
  note?: string | null;
  afterPass: boolean;
  /** 'second' or 'weekend' for an extra invitation. */
  option?: string | null;
}

interface CampaignZone {
  zone?: ZoneLabel | null;
  programme?: string | null;
  eventId?: string | null;
  eventTitle?: string | null;
  eventStartDate?: string | null;
  memberCount: number;
  members: CampaignMember[];
  extraMembers: CampaignMember[];
}

/**
 * Rows are identified by their event: several rows can share a colour, and the
 * row of members nothing is open to has neither an event nor a zone.
 */
const rowKey = (row: CampaignZone) => row.eventId ?? row.zone ?? "no-event";

/** What happened the last time this row was invited. */
interface ZoneResult {
  invited: number;
  skipped: number;
  warnings: string[];
}

const PROGRAMME_LABELS: Record<string, string> = {
  nmw: "Welcome",
  cxt: "CXT",
  bs: "Big Sister",
};

/** Event titles carry emoji; the table reads better without them. */
const cleanTitle = (title?: string | null) =>
  (title ?? "")
    .replace(/\p{Extended_Pictographic}️?/gu, "")
    .replace(/\s+/g, " ")
    .trim();

const memberLine = (member: CampaignMember) =>
  [
    member.firstName || member.email,
    member.isGuest ? "(Guest)" : "",
    member.why ? `— ${member.why}` : "",
    member.note ? `— ${member.note}` : "",
  ]
    .filter(Boolean)
    .join(" ");

/**
 * The campaigns, in the order they're worked through. Each is the same shape —
 * who's waiting, and what's on for her — so they share one screen rather than
 * one screen each.
 */
const CAMPAIGNS = [
  {
    key: "NMW",
    scope: "Welcome events, matched on title.",
    label: "New Member Welcome",
    description:
      "Little Sisters who joined in the last 28 days and haven't been to a Welcome event or been told about one, plus Guests with no Welcome ahead of them they can open. Each is matched to the soonest Welcome inside her own 7am to 10pm, rather than to whatever her colour zone happens to hold.",
  },
  {
    key: "CXT_BIG_SISTER",
    scope:
      "Public events open to unverified members in their first year, hosted by community success, support, a Big Sister or the founder — excluding Welcome events and beginner level.",
    label: "CXT & Big Sister",
    description:
      "New members in their first or second week who haven't been invited to, or turned up to, that queue's events that week, plus Guests with nothing of the kind ahead of them they can open. CXT and Big Sister are counted separately, so an invitation to one no longer silences the other.",
  },
  {
    key: "HOW_TO_STUDY",
    scope: "The monthly study session, matched on title, hosted by anyone other than a member.",
    label: "How to Study",
    description:
      "Little Sisters not yet invited to the monthly study session. Each is matched to whichever session falls inside her own waking day, nearest a comfortable evening — so the row she appears under is the session she can actually attend, which needn't be her usual colour.",
  },
  {
    key: "COACH",
    scope:
      "Public events hosted by an English Coach or the founder, of kind coach conversation, office hours, grammar spot, interview, study event or conversations over coffee — plus any featured event.",
    label: "Coach Events",
    description:
      "Coaching-plan members who've attended at least three events, matched to coach events at their level and within reach of their zone. One invitation per kind of event rather than one overall — already invited to a Grammar Spot means no more Grammar Spots, but conversation starters stay open. Shown a row per event, so the same member can appear more than once.",
  },
] as const;

const InviteCampaignsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [campaign, setCampaign] = useState<string>(CAMPAIGNS[0].key);
  const [tab, setTab] = useState<string>("invite");
  const chosen = CAMPAIGNS.find((entry) => entry.key === campaign) ?? CAMPAIGNS[0];

  const { loading, data, refetch } = useQuery(INVITE_CAMPAIGN, {
    variables: { campaign },
    fetchPolicy: "network-only",
  });

  const [validateInvite] = useMutation(VALIDATE_USERS_FOR_EMAIL_BULK_INVITE);
  const [bulkInvite] = useMutation(BULK_INVITE_WITH_USER_IDS);
  const [recordInvites] = useMutation(RECORD_CAMPAIGN_INVITES);

  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ZoneResult>>({});

  const rows: CampaignZone[] = data?.adminInviteCampaign ?? [];
  const totalWaiting = rows.reduce((sum, row) => sum + row.memberCount, 0);
  const placedRows = rows.filter((row) => row.eventId && row.memberCount > 0);

  /**
   * Invite one row's members to that row's event.
   *
   * Validate first, exactly as the bulk-invite screen does: it returns the ids
   * it would actually invite plus a warning for everyone it wouldn't (already
   * invited, not a member, the host). Inviting only the ids it hands back is
   * what makes running this twice harmless.
   */
  const inviteRow = async (row: CampaignZone, members: CampaignMember[], resultKey: string) => {
    if (!row.eventId || members.length === 0) {
      return;
    }
    const name = cleanTitle(row.eventTitle) || row.zone || "This row";

    setBusyRow(resultKey);
    try {
      const emails = members.map((member) => member.email);
      const validated = await validateInvite({
        variables: { eventId: row.eventId, input: { emails } },
      });

      const userIds: string[] = validated.data?.validateUsersForEmailBulkInvite?.userIds ?? [];
      const warnings: string[] =
        validated.data?.validateUsersForEmailBulkInvite?.warningMessages ?? [];

      if (userIds.length === 0) {
        setResults((prev) => ({
          ...prev,
          [resultKey]: { invited: 0, skipped: emails.length, warnings },
        }));
        enqueueSnackbar(`${name}: nobody left to invite.`, { variant: "info" });
        return;
      }

      const sent = await bulkInvite({
        variables: { eventId: row.eventId, input: { userIds } },
      });

      const sendWarnings: string[] = sent.data?.bulkInviteWithUserIds?.warningMessages ?? [];
      // What the server actually created, not what we asked it for. The mutation
      // revalidates and can still drop someone — already invited by another zone
      // a moment ago, made a host, gone inactive — so counting the request would
      // overstate the send and write outreach that never happened (Ana, on #4081).
      const invitedUserIds: string[] = sent.data?.bulkInviteWithUserIds?.invitedUserIds ?? [];

      // Write down who was invited, to which event, by which campaign. Recorded
      // after the send and only for the ids it actually went to, so the log
      // never claims an invitation that didn't happen. A failure here must not
      // read as a failed invite — she has already been invited by this point.
      if (invitedUserIds.length > 0) {
        try {
          await recordInvites({
            variables: { campaign, eventId: row.eventId, userIds: invitedUserIds },
          });
        } catch {
          enqueueSnackbar(`${row.zone}: invited, but couldn't write it to the outreach log.`, {
            variant: "warning",
          });
        }
      }

      setResults((prev) => ({
        ...prev,
        [resultKey]: {
          invited: invitedUserIds.length,
          skipped: emails.length - invitedUserIds.length,
          warnings: [...warnings, ...sendWarnings],
        },
      }));
      enqueueSnackbar(`${name}: invited ${invitedUserIds.length}.`, {
        variant: invitedUserIds.length > 0 ? "success" : "info",
      });

      // The invite itself is what makes them ineligible next time, so re-read
      // rather than trusting the screen to still be right.
      void refetch();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Invite failed.", {
        variant: "error",
      });
    } finally {
      setBusyRow(null);
    }
  };

  const membersCell = (members: CampaignMember[], extra: boolean) => {
    if (members.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          —
        </Typography>
      );
    }
    const guests = members.filter((member) => member.isGuest).length;
    return (
      <Tooltip
        title={<Box sx={{ whiteSpace: "pre-line" }}>{members.map(memberLine).join("\n")}</Box>}
      >
        <Box>
          <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
            {members
              .slice(0, 3)
              .map((member) => member.firstName || member.email)
              .join(", ")}
            {members.length > 3 ? ` +${members.length - 3} more` : ""}
          </Typography>
          <Stack direction="row" spacing={0.5} mt={0.25}>
            {guests > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${guests} Guest${guests === 1 ? "" : "s"}`}
              />
            )}
            {extra && (
              <Chip size="small" variant="outlined" color="info" label="extra invitation" />
            )}
          </Stack>
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <PageHeader title="Invite Campaigns">
        Members waiting to be invited, each matched to an event she can actually open: in her own
        day if she is a Guest or brand new, inside her Guest Pass, at her level, and never one she
        has said no to. Inviting goes through the usual checks, so anyone already asked is skipped
        rather than asked twice.
      </PageHeader>

      <Box mb={Spacing.m} display="flex" alignItems="flex-start" gap={Spacing.m} flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Campaign"
          value={campaign}
          onChange={(event) => setCampaign(event.target.value)}
          sx={{ minWidth: 260 }}
        >
          {CAMPAIGNS.map((entry) => (
            <MenuItem key={entry.key} value={entry.key}>
              {entry.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="textSecondary" maxWidth="60ch" mt={Spacing.s}>
          {chosen.description}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_event, next: string) => setTab(next)}
        sx={{ mb: Spacing.m, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="invite" label="Invite" />
        <Tab value="results" label="Results" />
      </Tabs>
      {tab === "results" && <CampaignResults campaign={campaign} scope={chosen.scope} />}
      {tab === "invite" &&
        (loading ? (
          <Box display="flex" justifyContent="center" p={Spacing.l}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box mb={Spacing.m}>
              <Typography variant="body2" color="textSecondary">
                {totalWaiting === 0
                  ? "Nobody is waiting in this campaign right now."
                  : `${totalWaiting} member${totalWaiting === 1 ? "" : "s"} waiting across ${
                      placedRows.length
                    } event${placedRows.length === 1 ? "" : "s"}.`}
              </Typography>
            </Box>

            <Card>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Zone</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell align="right">Waiting</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell align="right">Invite</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const key = rowKey(row);
                    const result = results[key];
                    const extraResult = results[`${key}:extra`];
                    const canInvite = Boolean(row.eventId) && row.members.length > 0;

                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {row.zone ? <Chip label={row.zone} size="small" /> : null}
                            {row.programme && PROGRAMME_LABELS[row.programme] ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={PROGRAMME_LABELS[row.programme]}
                              />
                            ) : null}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {row.eventId ? (
                            <>
                              <Typography variant="body2">{cleanTitle(row.eventTitle)}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {row.eventStartDate
                                  ? format(new Date(row.eventStartDate), "EEE d MMM, HH:mm")
                                  : ""}{" "}
                                UTC
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              {row.zone
                                ? "Nothing scheduled in this zone"
                                : "Nothing they can open in the next 3 weeks"}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="right">{row.memberCount}</TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            {membersCell(row.members, false)}
                            {row.extraMembers.length > 0 && membersCell(row.extraMembers, true)}
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!canInvite || busyRow !== null}
                              onClick={() => void inviteRow(row, row.members, key)}
                            >
                              {busyRow === key ? "Inviting…" : "Invite all"}
                            </Button>
                            {result ? (
                              <Typography variant="caption" display="block" color="textSecondary">
                                invited {result.invited}
                                {result.skipped > 0 ? `, skipped ${result.skipped}` : ""}
                              </Typography>
                            ) : null}
                            {row.extraMembers.length > 0 && (
                              <>
                                <Tooltip title="A nearer session than her best one, or a weekend session for a Guest whose pass has none. Sent only if you choose to.">
                                  <Button
                                    variant="text"
                                    size="small"
                                    disabled={busyRow !== null}
                                    onClick={() =>
                                      void inviteRow(row, row.extraMembers, `${key}:extra`)
                                    }
                                  >
                                    {busyRow === `${key}:extra`
                                      ? "Inviting…"
                                      : `Also invite ${row.extraMembers.length} extra`}
                                  </Button>
                                </Tooltip>
                                {extraResult ? (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="textSecondary"
                                  >
                                    invited {extraResult.invited}
                                    {extraResult.skipped > 0
                                      ? `, skipped ${extraResult.skipped}`
                                      : ""}
                                  </Typography>
                                ) : null}
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </>
        ))}
    </>
  );
};

export default InviteCampaignsPage;
